'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import '@/app/_styles/breadcrumbs.css'

/**
 * Breadcrumbs Component
 *
 * Renders breadcrumb navigation with optional JSON-LD schema.
 *
 * Schema handling:
 * - If `schema` prop is provided, uses that (pre-built via SchemaService)
 * - Otherwise generates schema inline (for backwards compatibility)
 * - Set showSchema=false to skip schema output entirely
 */
const Breadcrumbs = ({
    breadcrumbs,
    schema = null,
    product_id = false,
    className,
    on_white_background = true,
    showSchema = true
}) => {

    useEffect(() => {
        if (product_id) {
            // this component loads with server data and is populated
            // but these breadcrumbs need to be checked on every page load
            // and possibly changed to breadcrumbs that are consistent with the user's browsing experience
        }
    }, [product_id])

    // Use pre-built schema if provided, otherwise generate inline
    const breadcrumbSchema = schema || generateSchema(breadcrumbs)

    return (
        <>
            {showSchema && breadcrumbSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
                    key="breadcrumbs-jsonld"
                />
            )}
            {breadcrumbs && (
                <div className={className}>
                    <ul className={`breadcrumbs flex flex-wrap items-center mb-3 ${on_white_background ? 'white-background' : ''}`}>
                        {breadcrumbs.length === 1 && (
                            <li className="relative pr-2">
                                <Link
                                    className={`text-nowrap text-xs md:text-sm lg:text-xs ${on_white_background ? 'text-light' : 'text-white'}`}
                                    href="/"
                                >
                                    Home
                                </Link>
                            </li>
                        )}
                        {breadcrumbs.length > 0 &&
                            breadcrumbs.map((breadcrumb, index) => (
                                <li key={index} className="relative pr-2 group">
                                    <Link
                                        href={breadcrumb.url}
                                        className={`text-nowrap text-xs md:text-sm lg:text-xs group-last:text-primary ${on_white_background ? 'text-light' : 'text-white'} ${breadcrumb.sub_product_id ? 'capitalize' : ''}`}
                                    >
                                        {breadcrumb.name}
                                    </Link>
                                </li>
                            ))
                        }
                    </ul>
                </div>
            )}
        </>
    )
}

/**
 * Generate BreadcrumbList schema inline (backwards compatibility)
 * Prefer passing pre-built schema via SchemaService for new code
 */
function generateSchema(breadcrumbs) {
    if (!breadcrumbs || breadcrumbs.length === 0) {
        return null
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((breadcrumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: breadcrumb.name,
            item: breadcrumb.url
        }))
    }
}

export default Breadcrumbs
